import { FormEvent, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LanguageSwitch } from '@/components/LanguageSwitch';
import { customerIntakeService } from '@/services/customerIntakeService';
import { VehicleRelationship } from '@/types';

// Customers are never asked to categorize their own visit anymore (removed as
// friction), but the column backing this is NOT NULL with a check constraint
// (see 20260606020000_intake_hardening_atomic_resolution.sql), so every
// submission still sends this fixed, unseen default. Staff figure out the
// actual need in person; see CustomerIntake.tsx, which no longer displays
// this value since it's no longer customer-reported.
const DEFAULT_SERVICE_NEEDED = 'other';

const relationshipOptions: VehicleRelationship[] = [
  'mine',
  'wife',
  'son',
  'daughter',
  'father',
  'mother',
  'company',
  'other',
];

const RATE_GUARD_MS = 30_000;

function getLastSubmission(key: string) {
  try {
    return Number(localStorage.getItem(key) || 0);
  } catch {
    return 0;
  }
}

function rememberSubmission(key: string) {
  try {
    localStorage.setItem(key, String(Date.now()));
  } catch {
    // The server-side duplicate guard remains authoritative when browser storage is unavailable.
  }
}

// What the customer has typed survives a refresh in the same tab. It lives in
// sessionStorage (not localStorage) and is cleared on submit, so a shared
// counter tablet doesn't hand one customer's details to the next.
const DRAFT_FIELDS = ['full_name', 'phone', 'car_make_model', 'plate_number'] as const;

type RegisterCarDraft = Partial<Record<(typeof DRAFT_FIELDS)[number] | 'vehicle_relationship', string>>;

function readDraft(key: string): RegisterCarDraft {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(key) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeDraft(key: string, draft: RegisterCarDraft) {
  try {
    sessionStorage.setItem(key, JSON.stringify(draft));
  } catch {
    // Without storage the form still works; it just won't survive a refresh.
  }
}

function clearDraft(key: string) {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}

export default function RegisterCar() {
  const { t, i18n } = useTranslation();
  const { shopId: routeShopId } = useParams();
  const [searchParams] = useSearchParams();
  const shopId = (routeShopId || searchParams.get('shop') || import.meta.env.VITE_PUBLIC_SHOP_ID || '').trim();
  const draftKey = `wash-pump-register-car-draft:${shopId}`;
  const [draft] = useState(() => readDraft(draftKey));
  const formRef = useRef<HTMLFormElement>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [relationship, setRelationship] = useState<VehicleRelationship | ''>(() =>
    relationshipOptions.find((option) => option === draft.vehicle_relationship) ?? '',
  );
  const isRtl = i18n.language === 'ar';

  const formDirection = useMemo(() => (isRtl ? 'rtl' : 'ltr'), [isRtl]);

  const saveDraft = (form: HTMLFormElement, nextRelationship: VehicleRelationship | '' = relationship) => {
    const data = new FormData(form);
    writeDraft(draftKey, {
      ...Object.fromEntries(DRAFT_FIELDS.map((field) => [field, String(data.get(field) || '')])),
      vehicle_relationship: nextRelationship,
    });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setSubmitting(true);

    const data = new FormData(event.currentTarget);
    try {
      if (String(data.get('website') || '').trim()) {
        setSubmitted(true);
        return;
      }

      const rateGuardKey = `wash-pump-register-car:${shopId}`;
      const lastSubmission = getLastSubmission(rateGuardKey);
      if (lastSubmission && Date.now() - lastSubmission < RATE_GUARD_MS) {
        setError(t('registerCar.rateLimit'));
        return;
      }

      await customerIntakeService.submit({
        shop_id: shopId,
        full_name: String(data.get('full_name') || ''),
        phone: String(data.get('phone') || ''),
        car_make_model: String(data.get('car_make_model') || ''),
        plate_number: String(data.get('plate_number') || ''),
        service_needed: DEFAULT_SERVICE_NEEDED,
        vehicle_relationship: relationship || undefined,
      });
      rememberSubmission(rateGuardKey);
      clearDraft(draftKey);
      setSubmitted(true);
    } catch (submitError) {
      const message = submitError instanceof Error ? submitError.message : '';
      setError(message.toLowerCase().includes('wait before submitting') ? t('registerCar.rateLimit') : t('registerCar.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-background px-4 py-5 sm:py-10" dir={formDirection}>
      <div className="mx-auto max-w-md">
        <div className="mb-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Wash & Pump" className="h-12 w-12 object-contain rounded-2xl" />
            <div>
              <p className="text-lg font-extrabold">WASH &amp; PUMP</p>
              <p className="text-xs text-muted-foreground">{t('registerCar.brandHint')}</p>
            </div>
          </div>
          <LanguageSwitch />
        </div>

        <Card className="rounded-3xl">
          <CardContent className="p-5 sm:p-7">
            {!shopId ? (
              <div className="py-10 text-center">
                <AlertTriangle className="mx-auto mb-5 h-14 w-14 text-warning" />
                <h1 className="text-2xl font-extrabold">{t('registerCar.missingShopTitle')}</h1>
                <p className="mt-3 leading-relaxed text-muted-foreground">{t('registerCar.missingShopMessage')}</p>
              </div>
            ) : submitted ? (
              <div className="py-10 text-center">
                <CheckCircle2 className="mx-auto mb-5 h-16 w-16 text-success" />
                <h1 className="text-2xl font-extrabold">{t('registerCar.successTitle')}</h1>
                <p className="mt-3 leading-relaxed text-muted-foreground">{t('registerCar.successMessage')}</p>
              </div>
            ) : (
              <>
                <h1 className="text-2xl font-extrabold">{t('registerCar.title')}</h1>
                <p className="mt-2 leading-relaxed text-muted-foreground">{t('registerCar.subtitle')}</p>

                <form
                  ref={formRef}
                  className="mt-7 space-y-4"
                  onSubmit={handleSubmit}
                  onChange={(event) => saveDraft(event.currentTarget)}
                >
                  <div className="absolute -start-[10000px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
                    <Label htmlFor="website">Website</Label>
                    <Input id="website" name="website" tabIndex={-1} autoComplete="off" />
                  </div>
                  <div>
                    <Label htmlFor="full_name">{t('registerCar.fullName')} *</Label>
                    <Input id="full_name" name="full_name" defaultValue={draft.full_name} autoComplete="name" autoFocus required maxLength={100} className="mt-1.5 h-12 text-base" />
                  </div>
                  <div>
                    <Label htmlFor="phone">{t('registerCar.phone')} *</Label>
                    <Input id="phone" name="phone" defaultValue={draft.phone} type="tel" inputMode="tel" autoComplete="tel" required maxLength={30} className="mt-1.5 h-12 text-base" dir="ltr" />
                  </div>
                  <div>
                    <Label htmlFor="car_make_model">{t('registerCar.car')} *</Label>
                    <Input id="car_make_model" name="car_make_model" defaultValue={draft.car_make_model} required maxLength={100} placeholder={t('registerCar.carPlaceholder')} className="mt-1.5 h-12 text-base" />
                  </div>
                  <div>
                    <Label htmlFor="plate_number">{t('registerCar.plate')} *</Label>
                    <Input id="plate_number" name="plate_number" defaultValue={draft.plate_number} required maxLength={30} className="mt-1.5 h-12 text-base uppercase" dir="ltr" />
                  </div>
                  <div>
                    <Label>{t('registerCar.relationship')}</Label>
                    <Select
                      value={relationship}
                      onValueChange={(value) => {
                        setRelationship(value as VehicleRelationship);
                        if (formRef.current) saveDraft(formRef.current, value as VehicleRelationship);
                      }}
                    >
                      <SelectTrigger className="mt-1.5 h-12 text-base">
                        <SelectValue placeholder={t('registerCar.relationshipOptional')} />
                      </SelectTrigger>
                      <SelectContent>
                        {relationshipOptions.map((option) => (
                          <SelectItem key={option} value={option}>{t(`vehicleRelationships.${option}`)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {error && <p className="rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">{error}</p>}

                  <Button type="submit" size="lg" className="h-14 w-full text-base font-extrabold" disabled={submitting}>
                    {submitting && <Loader2 className="me-2 h-5 w-5 animate-spin" />}
                    {t('registerCar.submit')}
                  </Button>
                </form>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
